/**
 * Conversation grouping (spec §9 "Threads"): union-find over `messageId` / `references` /
 * `inReplyTo`; rows without ids fall back to a normalised subject; otherwise stand alone.
 */
import type { MessageRow, ThreadRow } from '$lib/types';

/** Reply/forward prefixes stripped before comparing subjects (with optional `[n]` and `:`/`：`). */
export const SUBJECT_PREFIX_RE =
  /^\s*(?:(?:re|fwd?|fw|رد|ردّ|تحويل|محوّلة|إعادة توجيه)\s*(?:\[\d+\])?\s*[:：]\s*)+/iu;

export function normalizeSubject(subject: string | null | undefined): string {
  return (subject || '').replace(SUBJECT_PREFIX_RE, '').trim().toLowerCase();
}

/** Sort clock: `receivedAt ?? date`. */
export function rowTime(row: Pick<MessageRow, 'date' | 'receivedAt'>): number {
  return Date.parse(row.receivedAt ?? row.date) || 0;
}

/**
 * Map each row key to its thread key: `id:<root message-id>`, else `subj:<normalised subject>`,
 * else `k:<row key>`.
 */
export function threadKeys(rows: readonly MessageRow[]): Map<string, string> {
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let cur = x;
    while (parent.get(cur) !== cur) {
      const p = parent.get(cur)!;
      parent.set(cur, parent.get(p)!);
      cur = parent.get(cur)!;
    }
    return cur;
  };
  const ensure = (x: string) => {
    if (!parent.has(x)) parent.set(x, x);
  };
  const union = (a: string, b: string) => {
    ensure(a);
    ensure(b);
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const r of rows) {
    if (!r.messageId) continue;
    ensure(r.messageId);
    for (const ref of [...(r.references ?? []), r.inReplyTo].filter(Boolean) as string[]) union(r.messageId, ref);
  }
  const out = new Map<string, string>();
  for (const r of rows) {
    const subj = normalizeSubject(r.subject);
    out.set(r.key, r.messageId ? 'id:' + find(r.messageId) : subj ? 'subj:' + subj : 'k:' + r.key);
  }
  return out;
}

/** Dress a single message as a list row. */
export function singleRow(row: MessageRow): ThreadRow {
  return { ...row, uids: [row.uid], threadId: row.key };
}

/**
 * Collapse rows into thread rows: the newest message represents the thread, flags are
 * `\Seen` only if every member is seen and `\Flagged` if any is, attachments/list are unions.
 * Input order is preserved for first appearance of each thread.
 */
export function buildThreadRows(rows: readonly MessageRow[]): ThreadRow[] {
  const keys = threadKeys(rows);
  const groups = new Map<string, MessageRow[]>();
  const order: string[] = [];
  for (const r of rows) {
    const k = keys.get(r.key)!;
    if (!groups.has(k)) {
      groups.set(k, []);
      order.push(k);
    }
    groups.get(k)!.push(r);
  }
  return order.map((k) => {
    const members = groups.get(k)!;
    if (members.length === 1) return { ...singleRow(members[0]!), threadId: k };
    const newest = members.reduce((best, m) => (rowTime(m) > rowTime(best) ? m : best));
    const flags = new Set<string>();
    if (members.every((m) => m.flags.includes('\\Seen'))) flags.add('\\Seen');
    if (members.some((m) => m.flags.includes('\\Flagged'))) flags.add('\\Flagged');
    return {
      ...newest,
      flags: [...flags],
      hasAttachments: members.some((m) => m.hasAttachments),
      atts: [...new Set(members.flatMap((m) => m.atts ?? []))].slice(0, 3),
      list: members.some((m) => m.list),
      uids: members.map((m) => m.uid),
      tcount: members.length,
      threadId: k,
    };
  });
}

/**
 * All messages sharing a thread with `uid` in `folder`, newest first — or `[]` when the message
 * stands alone (the reader shows thread items only then).
 */
export function threadOf(rows: readonly MessageRow[], uid: number, folder: string): MessageRow[] {
  const me = rows.find((r) => r.uid === uid && r.folder === folder);
  if (!me) return [];
  const keys = threadKeys(rows);
  const k = keys.get(me.key);
  const members = rows.filter((r) => keys.get(r.key) === k);
  return members.length > 1 ? [...members].sort((a, b) => rowTime(b) - rowTime(a)) : [];
}
